import { useEffect, useState } from 'react';

export default function Hero() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      className="relative w-full min-h-screen flex items-center overflow-hidden"
      aria-label="Hero — Carpet & Flooring Transformers"
    >
      {/* Background image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/1e700e3b6cd761cb3fe68a4350125746.jpg"
          alt="Premium carpet and flooring installation — Carpet & Flooring Transformers Snellville GA"
          className="w-full h-full object-cover object-center"
          onLoad={() => setLoaded(true)}
        />
        {/* Multi-layer cinematic overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/92 via-stone-950/70 to-stone-950/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 via-transparent to-stone-950/20"></div>
      </div>

      {/* Decorative vertical line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-amber-500/60 to-transparent hidden lg:block"></div>

      {/* Content */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-14 pt-28 pb-24 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <div className="max-w-[680px]">

          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2.5 bg-amber-500/15 border border-amber-500/30 backdrop-blur-sm rounded-full px-5 py-2 mb-10">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></div>
            <span className="text-amber-300 text-[11px] font-bold tracking-[0.18em] uppercase">
              Snellville, GA &mdash; Serving Metro Atlanta
            </span>
          </div>

          {/* H1 — single, strong */}
          <h1 className="font-serif text-[52px] md:text-[64px] lg:text-[76px] text-white leading-[1.04] mb-7 tracking-tight">
            Floors That<br />
            <span className="relative inline-block">
              <em className="not-italic text-amber-400">Transform</em>
            </span>
            <br />
            <span className="text-white/80 text-[0.75em]">Your Home.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-white/65 text-lg md:text-xl leading-relaxed mb-11 max-w-[500px]">
            Metro Atlanta&apos;s full-service flooring specialists — carpet cleaning, repair, installation, and hard surface restoration. Backed by a 100% satisfaction guarantee.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3.5 mb-14">
            <a
              href="tel:7702361255"
              className="inline-flex items-center justify-center gap-2.5 bg-amber-500 hover:bg-amber-400 text-stone-950 font-extrabold text-[15px] px-9 py-4.5 rounded-full transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-amber-500/20"
              style={{ paddingTop: '1.125rem', paddingBottom: '1.125rem' }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-base"></i>
              </div>
              Call 770.236.1255
            </a>
            <a
              href="#estimate-form"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/60 bg-white/8 hover:bg-white/12 backdrop-blur-sm text-white font-semibold text-[15px] px-9 py-4.5 rounded-full transition-all whitespace-nowrap cursor-pointer"
              style={{ paddingTop: '1.125rem', paddingBottom: '1.125rem' }}
            >
              Request Free Estimate
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>

          {/* Trust signals row */}
          <div className="flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-8">
            {[
              { icon: 'ri-star-fill', label: '5-Star Google Rated' },
              { icon: 'ri-shield-check-fill', label: '100% Satisfaction Guarantee' },
              { icon: 'ri-home-heart-fill', label: 'Family Owned & Operated' },
              { icon: 'ri-leaf-fill', label: 'Eco-Friendly Products' },
            ].map((t) => (
              <div key={t.label} className="flex items-center gap-2 text-white/50 text-sm">
                <div className="w-4 h-4 flex items-center justify-center text-amber-400 flex-shrink-0">
                  <i className={`${t.icon} text-sm`}></i>
                </div>
                <span className="whitespace-nowrap">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side — floating card (desktop only) */}
      <div className="absolute right-10 bottom-16 hidden xl:flex flex-col gap-3 z-10">
        <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-5 w-[220px]">
          <div className="flex items-center gap-2 mb-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="w-3.5 h-3.5 flex items-center justify-center text-amber-400">
                <i className="ri-star-fill text-xs"></i>
              </div>
            ))}
          </div>
          <p className="text-white text-xs leading-relaxed">&ldquo;Best flooring company in Snellville. Absolutely transformed our home.&rdquo;</p>
          <p className="text-white/40 text-[10px] mt-2">— Jennifer M., Snellville GA</p>
        </div>
        <div className="bg-amber-500/20 backdrop-blur-md border border-amber-500/30 rounded-2xl p-4 w-[220px] flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0">
            <i className="ri-phone-fill text-stone-950 text-sm"></i>
          </div>
          <div>
            <p className="text-white/50 text-[10px] uppercase tracking-widest">Call Now</p>
            <a href="tel:7702361255" className="text-white font-bold text-sm cursor-pointer">770.236.1255</a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 z-10">
        <div className="w-px h-12 bg-gradient-to-b from-white/0 to-white/25"></div>
        <div className="w-1.5 h-1.5 rounded-full bg-white/30"></div>
      </div>
    </section>
  );
}
